import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faLayerGroup,
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { Col } from "reactstrap";
import { PulseLoader } from "react-spinners";

import { APIMS } from "../../../../../../../../config/apims";
import { APISD } from "../../../../../../../../config/apisd";
import ToastError from "../../../../../../../NotificationToast/ToastError";
import ModalAdd from "./modal/add";
import ModalUpdate from "./modal/update";

import "bulma/css/bulma.css";
import "../../../../../../../../index.css";

// const pageStyles = `
//   .fase-grade-search-section {
//     margin-bottom: 14px;
//   }

//   .fase-grade-table {
//     min-width: 760px;
//   }

//   .fase-grade-table .fase-grade-col-number {
//     width: 76px;
//   }

//   .fase-grade-table .fase-grade-col-level {
//     width: 20%;
//   }

//   .fase-grade-table .fase-grade-col-school {
//     width: auto;
//   }

//   .fase-grade-table .fase-grade-col-branch {
//     width: 33%;
//   }

//   .fase-grade-table .fase-grade-col-action {
//     width: 140px;
//     text-align: center !important;
//   }

//   .fase-grade-main-text,
//   .fase-grade-secondary-text {
//     display: block;
//     width: 100%;
//     line-height: 1.5;
//     white-space: normal;
//     overflow-wrap: break-word;
//     word-break: normal;
//   }

//   .fase-grade-main-text {
//     color: #24314e;
//     font-size: 13px;
//     font-weight: 700;
//   }

//   .fase-grade-secondary-text {
//     color: #69758f;
//     font-size: 13px;
//     font-weight: 500;
//   }

//   @media (max-width: 780px) {
//     .fase-grade-table {
//       min-width: 700px;
//     }

//     .fase-grade-page .page-header-top {
//       align-items: flex-start !important;
//       flex-direction: column;
//       gap: 14px;
//     }

//     .fase-grade-page .page-header-top .ml-auto {
//       width: 100%;
//       margin-left: 0 !important;
//     }

//     .fase-grade-page .module-header-button {
//       width: 100%;
//       justify-content: center;
//     }
//   }
// `;

const getGradeId = (item) =>
  item?.ysb_grade_level_id || item?.grade_level_id || item?.id || "";

const normalizeValue = (value) =>
  String(value === null || value === undefined ? "" : value)
    .trim()
    .toUpperCase();

export default function FaseGrade() {
  document.title = "Konfigurasi Tingkatan Fase";

  const { id: faseId, name: routeName } = useParams();
  const token = localStorage.getItem("token");
  const storageLevel = localStorage.getItem("level") || "developer";

  const [faseDetail, setFaseDetail] = useState(null);
  const [getData, setGetData] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFetching, setIsFetching] = useState(false);

  const [modalAdd, setModalAdd] = useState(false);
  const [modalUpdate, setModalUpdate] = useState(false);
  const [dataUpdate, setDataUpdate] = useState(null);

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  const GetResponseData = useCallback(async () => {
    if (!faseId) {
      setFaseDetail(null);
      setGetData([]);
      return;
    }

    setIsFetching(true);

    const faseRequestBody = {
      page: 1,
      limit: 1000,
      ascending: 1,
      search: "",
      level: storageLevel,
    };

    const gradeRequestBody = {
      page: 1,
      limit: 10000,
      ascending: 1,
      search: "",
      level: storageLevel,
    };

    try {
      const [faseResponse, gradeResponse] = await Promise.all([
        APISD.get(`/api/fases/${faseId}`, {
          ...fetchParams,
          params: faseRequestBody,
          data: faseRequestBody,
        }),
        APIMS.get("/api/grade-levels", {
          ...fetchParams,
          params: gradeRequestBody,
          data: gradeRequestBody,
        }),
      ]);

      const detail = faseResponse?.data?.data || null;
      const assignedGradeLevels = Array.isArray(detail?.grade_level)
        ? detail.grade_level
        : [];
      const masterGradeLevels = gradeResponse?.data?.data || [];

      const enrichedGradeLevels = assignedGradeLevels.map((assigned) => {
        const assignedId = getGradeId(assigned);
        const master = masterGradeLevels.find(
          (grade) =>
            normalizeValue(grade?.id) === normalizeValue(assignedId)
        );

        return {
          ...master,
          ...assigned,
          id: assignedId,
          ysb_grade_level_id: assignedId,
          name_level:
            assigned?.name_level || master?.name_level || "-",
          ysb_branch_id:
            assigned?.ysb_branch_id || master?.ysb_branch_id || "",
          ysb_school_id:
            assigned?.ysb_school_id || master?.ysb_school_id || "",
          name_branch:
            assigned?.name_branch || master?.name_branch || "",
          name_school:
            assigned?.name_school || master?.name_school || "",
        };
      });

      setFaseDetail(detail);
      setGetData(enrichedGradeLevels);
    } catch (error) {
      setFaseDetail(null);
      setGetData([]);

      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          "Gagal memuat konfigurasi tingkatan fase",
      });
    } finally {
      setIsFetching(false);
    }
  }, [faseId, fetchParams, storageLevel]);

  useEffect(() => {
    GetResponseData();
  }, [GetResponseData]);

  const faseName = useMemo(() => {
    if (faseDetail?.name_fase) {
      return faseDetail.name_fase;
    }

    try {
      return decodeURIComponent(routeName || "");
    } catch (error) {
      return routeName || "";
    }
  }, [faseDetail?.name_fase, routeName]);

  const nameCollator = useMemo(
    () =>
      new Intl.Collator("id", {
        sensitivity: "base",
        numeric: true,
      }),
    []
  );

  const filteredData = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    const source = Array.isArray(getData) ? getData : [];

    const rows = !keyword
      ? source
      : source.filter((item) => {
          const levelName = String(item?.name_level || "").toLowerCase();
          const schoolName = String(item?.name_school || "").toLowerCase();
          const schoolCode = String(item?.ysb_school_id || "").toLowerCase();
          const branchName = String(item?.name_branch || "").toLowerCase();
          const branchCode = String(item?.ysb_branch_id || "").toLowerCase();

          return (
            levelName.includes(keyword) ||
            schoolName.includes(keyword) ||
            schoolCode.includes(keyword) ||
            branchName.includes(keyword) ||
            branchCode.includes(keyword)
          );
        });

    return [...rows].sort((a, b) => {
      const branchCompare = nameCollator.compare(
        String(a?.ysb_branch_id || ""),
        String(b?.ysb_branch_id || "")
      );

      if (branchCompare !== 0) {
        return branchCompare;
      }

      const schoolCompare = nameCollator.compare(
        String(a?.name_school || a?.ysb_school_id || ""),
        String(b?.name_school || b?.ysb_school_id || "")
      );

      if (schoolCompare !== 0) {
        return schoolCompare;
      }

      return nameCollator.compare(
        String(a?.name_level || ""),
        String(b?.name_level || "")
      );
    });
  }, [getData, nameCollator, searchKeyword]);

  const totalRows = filteredData.length;

  const totalPages = useMemo(() => {
    const pages = Math.ceil(totalRows / Math.max(1, rowsPerPage));
    return pages > 0 ? pages : 1;
  }, [rowsPerPage, totalRows]);

  useEffect(() => {
    setTablePage(1);
  }, [searchKeyword]);

  useEffect(() => {
    if (tablePage > totalPages) {
      setTablePage(totalPages);
    }

    if (tablePage < 1) {
      setTablePage(1);
    }
  }, [tablePage, totalPages]);

  const paginatedData = useMemo(() => {
    const start = (tablePage - 1) * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, rowsPerPage, tablePage]);

  const pageNumbers = useMemo(() => {
    const maxButtons = 5;
    const pages = [];

    if (totalPages <= maxButtons) {
      for (let page = 1; page <= totalPages; page += 1) {
        pages.push(page);
      }
      return pages;
    }

    const left = Math.max(1, tablePage - 1);
    const right = Math.min(totalPages, tablePage + 1);

    pages.push(1);

    if (left > 2) {
      pages.push("...");
    }

    for (let page = left; page <= right; page += 1) {
      if (page !== 1 && page !== totalPages) {
        pages.push(page);
      }
    }

    if (right < totalPages - 1) {
      pages.push("...");
    }

    pages.push(totalPages);

    return pages.filter(
      (value, index, array) => array.indexOf(value) === index
    );
  }, [tablePage, totalPages]);

  const goToPage = (nextPage) => {
    const safePage = Math.min(
      Math.max(1, Number(nextPage) || 1),
      totalPages
    );

    setTablePage(safePage);
  };

  const openUpdateModal = (row) => {
    setDataUpdate(row);
    setModalUpdate(true);
  };

  const closeUpdateModal = () => {
    setModalUpdate(false);
    setDataUpdate(null);
  };

  const startRow =
    totalRows === 0
      ? 0
      : Math.min((tablePage - 1) * rowsPerPage + 1, totalRows);
  const endRow = Math.min(tablePage * rowsPerPage, totalRows);

  return (
    <div className="body sims-page">
      {/* <style>{pageStyles}</style> */}

      {modalAdd && (
        <ModalAdd
          faseId={faseId}
          faseName={faseName}
          existingGradeLevels={getData}
          GetResponseData={GetResponseData}
          show={modalAdd}
          onHide={() => setModalAdd(false)}
        />
      )}

      {modalUpdate && (
        <ModalUpdate
          faseId={faseId}
          faseName={faseName}
          dataUpdate={dataUpdate}
          allAssignedGrades={getData}
          GetResponseData={GetResponseData}
          show={modalUpdate}
          onHide={closeUpdateModal}
        />
      )}

      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faLayerGroup} />
            </span>

            <h4 className="page-title-text">
              Tingkatan Fase {faseName || "-"}
            </h4>
          </div>

          <div className="ml-auto">
            <button
              type="button"
              onClick={() => setModalAdd(true)}
              className="btn btn-create sims-header-button"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Tambah Tingkatan</span>
            </button>
          </div>
        </div>

        <nav
          aria-label="breadcrumb"
          className="sims-breadcrumb-nav mt-4"
        >
          <ol className="sims-breadcrumb">
            <li className="sims-breadcrumb-item">
              <Link to="/matpels">Mata Pelajaran</Link>
            </li>

            <li className="sims-breadcrumb-separator">/</li>

            <li className="sims-breadcrumb-item">
              <Link to="/fase">Fase</Link>
            </li>

            <li className="sims-breadcrumb-separator">/</li>

            <li
              className="sims-breadcrumb-item active"
              aria-current="page"
            >
              Tingkatan {faseName || "-"}
            </li>
          </ol>
        </nav>
      </div>

      <div className="body-content sims-content">
        {isFetching ? (
          <div className="sims-loading">
            <PulseLoader color="#3f5bea" size={12} />
          </div>
        ) : (
          <Col xl="12" sm="12" className="sims-column">
            <div className="sims-search-wrap mb-3">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="sims-search-icon"
              />

              <input
                type="text"
                className="sims-search-input"
                placeholder="Cari tingkatan, sekolah, atau cabang..."
                value={searchKeyword}
                onChange={(event) => setSearchKeyword(event.target.value)}
              />
            </div>

            <div className="sims-table-card">
              <div className="sims-table-scroll">
                <table className="sims-table">
                  <thead>
                    <tr>
                      <th className="sims-col-number">
                        No
                      </th>
                      <th style={{width: "20%"}} >Tingkatan</th>
                      <th>Sekolah</th>
                      <th>Cabang</th>
                      <th className="sims-col-action">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((row, index) => (
                        <tr key={getGradeId(row) || index}>
                          <td className="sims-col-number">
                            <span className="sims-number-badge">
                              {(tablePage - 1) * rowsPerPage + index + 1}
                            </span>
                          </td>

                          <td>
                            <span className="sims-text-bold" style={{paddingLeft: 23}}>
                              {row?.name_level || "-"}
                            </span>
                          </td>

                          <td>
                            <span >
                              {row?.name_school || row?.ysb_school_id || "-"}
                            </span>
                          </td>

                          <td>
                            <span >
                              {row?.name_branch || row?.ysb_branch_id || "-"}
                            </span>
                          </td>

                          <td className="sims-col-action">
                            <div className="sims-action-wrap">
                              <button
                                type="button"
                                className="sims-action-button sims-action-edit"
                                onClick={() => openUpdateModal(row)}
                                title="Ubah konfigurasi tingkatan"
                                aria-label={`Ubah tingkatan ${
                                  row?.name_level || "fase"
                                }`}
                              >
                                <FontAwesomeIcon icon={faPenToSquare} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="sims-empty-state">
                          <div className="sims-empty-state-content">
                            Belum ada tingkatan yang dikonfigurasikan pada fase
                            ini
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {filteredData.length > 0 && (
                <div className="sims-pagination">
                  <div className="sims-pagination-left">
                    <span className="sims-page-info">
                      Menampilkan <b>{startRow}</b>-<b>{endRow}</b> dari{" "}
                      <b>{totalRows}</b> data
                    </span>

                    <select
                      className="sims-page-size"
                      value={rowsPerPage}
                      onChange={(event) => {
                        const value = Number(event.target.value) || 10;
                        setRowsPerPage(value);
                        setTablePage(1);
                      }}
                    >
                      <option value={10}>10 / halaman</option>
                      <option value={25}>25 / halaman</option>
                      <option value={50}>50 / halaman</option>
                      <option value={100}>100 / halaman</option>
                    </select>
                  </div>

                  <div className="sims-pagination-right">
                    <button
                      type="button"
                      className="sims-page-nav"
                      onClick={() => goToPage(tablePage - 1)}
                      disabled={tablePage <= 1}
                      aria-label="Halaman sebelumnya"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                      <span>Sebelumnya</span>
                    </button>

                    <div className="sims-page-numbers">
                      {pageNumbers.map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`dots-${index}`}
                            className="sims-page-dots"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            type="button"
                            key={`page-${page}`}
                            className={`sims-page-number ${
                              page === tablePage ? "active" : ""
                            }`}
                            onClick={() => goToPage(page)}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      type="button"
                      className="sims-page-nav"
                      onClick={() => goToPage(tablePage + 1)}
                      disabled={tablePage >= totalPages}
                      aria-label="Halaman berikutnya"
                    >
                      <span>Selanjutnya</span>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Col>
        )}
      </div>
    </div>
  );
}
